import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { CopyCheck, Archive, ExternalLink, MapPin, Sparkles } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
};

export const DuplicatesManager = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["adminDuplicates"],
    queryFn: async () => (await api.get("/admin/events/duplicates")).data,
  });

  const archive = useMutation({
    mutationFn: async (slug) => (await api.post(`/admin/events/${slug}/archive`)).data,
    onSuccess: () => {
      toast.success("Événement archivé");
      qc.invalidateQueries({ queryKey: ["adminDuplicates"] });
      qc.invalidateQueries({ queryKey: ["adminEvents"] });
      qc.invalidateQueries({ queryKey: ["adminDashboard"] });
      qc.invalidateQueries({ queryKey: ["events"] });
    },
    onError: () => toast.error("Action impossible"),
  });

  const groups = data?.groups || [];

  return (
    <div data-testid="duplicates-manager">
      <h2 className="font-serif text-2xl text-stone-100 mt-14 mb-2 flex items-center gap-2">
        <CopyCheck className="w-5 h-5 text-amber-500" /> Doublons potentiels ({groups.length})
      </h2>
      <p className="text-sm text-stone-400 mb-5 max-w-2xl">
        Événements qui semblent en double (même date + même ville/lieu et titre similaire). Le plus ancien est
        conservé par défaut — archivez celui que vous voulez retirer de l'agenda (réversible).
      </p>

      {isLoading ? (
        <div className="py-10 text-center text-stone-500" data-testid="duplicates-loading">Analyse en cours…</div>
      ) : groups.length === 0 ? (
        <div className="flex items-center gap-2 justify-center py-14 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="duplicates-empty">
          <Sparkles className="w-4 h-4 text-amber-500/70" /> Aucun doublon détecté — l'agenda est propre.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group, gi) => (
            <div key={gi} data-testid={`dup-group-${gi}`} className="bg-[#121418] border border-amber-800/40 rounded-lg p-4">
              <div className="font-mono text-[11px] uppercase tracking-wider text-amber-500/90 mb-3">
                {(group[0]?.start_date || "").slice(0, 10)} · {group[0]?.city || group[0]?.venue} — {group.length} entrées
              </div>
              <div className="space-y-2">
                {group.map((e, i) => (
                  <div key={e.slug} data-testid={`dup-event-${e.slug}`}
                    className={`flex items-center gap-3 rounded-md p-2.5 border ${i === 0 ? "border-emerald-800/40 bg-emerald-950/20" : "border-[#272A34] bg-[#181A20]"}`}>
                    {e.image ? <img src={e.image} alt="" className="w-12 h-9 rounded object-cover shrink-0" /> : <div className="w-12 h-9 rounded bg-[#101216] shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-stone-200 truncate flex items-center gap-2">
                        {e.title}
                        {i === 0 && <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-400 border border-emerald-800/50 rounded px-1 py-0.5">à conserver</span>}
                      </div>
                      <div className="text-xs text-stone-500 truncate flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" />{[e.venue || e.city, fmtDate(e.start_date), e.status, e.source_name].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <a href={`/events/${e.slug}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`dup-view-${e.slug}`}
                      className="p-1.5 rounded-md bg-[#101216] border border-[#272A34] text-stone-400 hover:text-stone-200 shrink-0" title="Voir">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button data-testid={`dup-archive-${e.slug}`} disabled={archive.isPending}
                      onClick={() => archive.mutate(e.slug)}
                      className="inline-flex items-center gap-1 bg-amber-900/40 border border-amber-700/50 text-amber-300 hover:bg-amber-800/50 disabled:opacity-40 px-3 py-1.5 rounded-md text-xs shrink-0" title="Archiver">
                      <Archive className="w-3.5 h-3.5" /> Archiver
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DuplicatesManager;
